import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Box} from '@mui/material';
import {FormProvider, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {t} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import './ReProductionComponents.scss';

import {TiffComponentModel} from '../../../../api/types/production.type';
import {
  FormAction,
  FormMode,
  ModuleType,
  ToastType,
  ProductionStatus,
  ReProductionFormData,
  getReProductionEditabilityRules,
  //ReProductionType,
  transformTagTreeData,
} from '../ReProductionUtils';
import {
  ReProductionComponentsFormData,
  reproductionComponentsSchema,
} from '../../../../schemas/productions/reProductionSchema';
import {
  setFillerAudioFileList,
  setMP3DataComponent,
  setRedactionTagList,
  setSecurityGroupId,
  setProductionSetSetId,
} from '../../../../redux/slice/productionSlice';
import {setReproductionIsFieldsChanged} from '../../../../redux/slice/reproductionSlice'; // ← ADD import
import {
  useGetReProductionComponents,
  useSaveReProductionComponents,
} from '../../../../hooks/productions/useReProductions';
//import {ReProductionComponentsFormData} from '../../../../schemas/productions/productionSchema';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrAccordionWithCheckbox from '../../../common/arr-accordion-with-checkbox/ArrAccordionWithCheckbox';
import {
  ImagePlaceholderItem,
  TiffPdfValues,
} from './tiff-pdf-component/TiffPdfComponent';
import DatComponent from './dat-component/DatComponent';
import {NativeComponent} from './native-components/NativeComponent';
import TiffPdfComponent from './tiff-pdf-component/TiffPdfComponent';
import TextComponent from './text-component/TextComponent';

// ── Constants ────────────────────────────────────────────────────────────────
// Component Constants
// DAT field category constants
const DAT_FIELD_CATEGORY_FIELD = 'Field';
const DAT_FIELD_CATEGORY_TAGS = 'Tags';

// DAT field type constants
const DAT_FIELD_TYPE_FIELDS = 'FIELDS';
const DAT_FIELD_TYPE_TAGS = 'TAGS';

// Default DAT field constants
const DEFAULT_DAT_SOURCE_FIELD_ID = '-146';
const DEFAULT_DAT_FIELD_HEADER_NAME = 'ProdBeginningBates';
interface StepChildRef {
  save: () => void;
  next: () => void;
}
interface ReProductionComponentsProps {
  formData: ReProductionFormData;
  productionId: number;
  isProductionSet?: boolean;
  exportProductionId?: number;
  getStatus: (isShowToast: boolean, status: ToastType, message: string) => void;
  goNext: () => void;
  goBack: () => void;
  onStateChange: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  productionStatusCode?: string;
  nativePlaceholderBaselineRef?: React.MutableRefObject<ImagePlaceholderItem[]>;
}

const ReProductionComponents = forwardRef<
  StepChildRef,
  ReProductionComponentsProps
>(
  (
    {
      formData,
      productionId,
      isProductionSet = true,
      exportProductionId = 0,
      getStatus,
      goNext,
      onStateChange,
      productionStatusCode,
      nativePlaceholderBaselineRef,
    },
    ref
  ) => {
    useTranslation();
    const dispatch = useDispatch();
    const {productionSetId, securityGroupId} = useSelector(
      (state: RootState) => state.production
    );

    const reProductionType = formData.reProductionType;
    const moduleType = ModuleType.REPRODUCTION; //'PRODUCTION';//
    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode === ProductionStatus.INPROGRESS;

    // ── Bootstrap redux ───────────────────────────────────────────────────────
    useEffect(() => {
      if (!securityGroupId) dispatch(setSecurityGroupId(1));
      if (!productionSetId) dispatch(setProductionSetSetId(1));
    }, []);

    // ── Reproduction editability rules ────────────────────────────────────────
    const rules = useMemo(
      () => getReProductionEditabilityRules(reProductionType ?? undefined),
      [reProductionType]
    );

    const editableSections = useMemo(() => {
      const sections: string[] = [];
      if (rules?.native?.contentMode === 'edit') sections.push('Native');
      if (
        rules?.tiffPdf?.contentMode === 'edit' // ← FIXED: was rules?.tiff
      )
        sections.push('TIFF / PDF');
      return sections;
    }, [rules]);

    // When re-production is in-progress or completed, every sub-section is locked.
    // The API-driven status takes precedence over per-type editability rules.
    const scrubEditable = isInProgressOrCompleted
      ? false
      : (rules?.dat?.subSections?.scrubbing ?? false);
    const disableGenerateLST =
      isInProgressOrCompleted ||
      rules?.native?.subSections?.generateLst === false;

    // ── Derive editable TIFF sub-sections from sub-section flags ─────────────
    const editableTiffSubSections = useMemo<string[]>(() => {
      if (isInProgressOrCompleted) return []; // all locked when in-progress/completed
      if (!rules?.tiffPdf) return [];
      if (rules.tiffPdf.contentMode === 'view') return [];

      const sub = rules.tiffPdf.subSections;
      const editable: string[] = [];

      if (sub.pageSettings) editable.push('pageOptions', 'toggles');
      if (sub.branding) editable.push('branding');
      if (sub.placeholders) editable.push('placeholders'); // ← IMAGE_NATIVE_TO_PLACEHOLDER, PLACEHOLDER_TO_IMAGE_NATIVE
      if (sub.redactions) editable.push('redactions'); // ← ADD_REMOVE_REDACTIONS

      return editable;
    }, [rules, isInProgressOrCompleted]);

    const editableNativeSubSections = useMemo<string[]>(() => {
      if (isInProgressOrCompleted) return [];
      if (!rules?.native) return [];
      if (rules.native.contentMode === 'view') return [];

      const sub = rules.native.subSections;
      const editable: string[] = [];

      if (sub.fileSettings) editable.push('fileSettings');
      if (sub.redactions) editable.push('redactions');
      if (sub.generateLst) editable.push('generateLst');

      return editable;
    }, [rules, isInProgressOrCompleted]);

    const getSectionMode = useCallback(
      (sectionId: string): FormMode =>
        isInProgressOrCompleted
          ? FormMode.VIEW
          : editableSections.includes(sectionId)
            ? FormMode.CREATE
            : FormMode.VIEW,
      [editableSections, isInProgressOrCompleted]
    );

    // ── Form — same pattern as ProductionComponents ───────────────────────────
    // const schema = useMemo(() => buildReProductionComponentsSchema(), []);
    const schema = useMemo(() => {
      const isNativeFileSettingsEditable =
        !isInProgressOrCompleted &&
        Boolean(rules?.native?.subSections?.fileSettings);

      const messages = {
        datRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datRequired'
        ),
        atLeastOneFieldRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.atLeastOneFieldRequired'
        ),
        prodBeginningBatesRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.prodBeginningBatesRequired'
        ),
        prodBeginningBatesOrDocIdRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.prodBeginningBatesOrDocIdRequired'
        ),
        tagsRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.tagsRequired'
        ),
        fieldRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.fieldRequired'
        ),
        customBrandingTagsAndTextRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.customBrandingTagsAndTextRequired'
        ),
        customBrandingTagsRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.customBrandingTagsRequired'
        ),
        customBrandingContentRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.customBrandingContentRequired'
        ),
        datFieldsRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldsRequired'
        ),
        datFieldNameRequired: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameRequired'
        ),
        datFieldNameAlphabetStart: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameAlphabetStart'
        ),
        datFieldNameSpecialChars: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameSpecialChars'
        ),
        datFieldNameDuplicate: t(
          'productionsAndExports.createNewProduction.datComponent.errors.datFieldNameDuplicate'
        ),
        placeholderPrivRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.placeholderPrivRequired'
        ),
        placeholderTechIssueRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.placeholderTechIssueRequired'
        ),
        placeholderNativeDocsRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.placeholderNativeDocsRequired'
        ),
        redactionTagsRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.redactionTagsRequired'
        ),
        redactionTextRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.errors.redactionTextRequired'
        ),
        generateMP3LoadFileRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.mp3.errors.generateMP3LoadFileRequired'
        ),
        mP3RedactionTagsEmpty: t(
          'productionsAndExports.createNewProduction.productionComponents.mp3.errors.mP3RedactionTagsEmpty'
        ),
        nativeFileGroupOrTagRequired: t(
          'productionsAndExports.createNewProduction.productionComponents.native.errors.nativeFileGroupOrTagRequired'
        ),
      };

      return reproductionComponentsSchema(
        messages,
        moduleType,
        isNativeFileSettingsEditable
      );
    }, [moduleType, t, isInProgressOrCompleted, rules]);
    const defaultValues = schema.parse({
      // For PRODUCTION, DAT component should be selected by default.
      // For EXPORT, DAT is optional and will start unchecked unless API says otherwise.
      datSelected: moduleType === 'REPRODUCTION',
      datFields: [
        {
          id: 1,
          category: DAT_FIELD_CATEGORY_FIELD,
          sourceField: DEFAULT_DAT_SOURCE_FIELD_ID,
          datFieldHeaderName: DEFAULT_DAT_FIELD_HEADER_NAME,
          scrubRedactions: false,
          scrubPriv: false,
          selectedTags: [],
        },
      ],
      nativeSelected: false, // Set to false initially to avoid validation error on mount
      nativeSelectedFileGroups: '',
      nativeSelectedTags: '',
    });

    const methods = useForm({
      mode: 'onChange',
      resolver: zodResolver(schema),
      defaultValues,
    });
    const {
      setValue,
      handleSubmit,
      formState,
      trigger,
      watch,
      getValues,
      reset,
    } = methods;
    const {isSubmitting, isValid, isDirty, errors} = formState;

    const mP3ComponentModelData = watch('mP3ComponentModelData');
    const mP3IsBurnRedactionSelected = watch(
      'mP3ComponentModelData.isBurnRedactionSelected'
    );
    const mP3IsRedactionByAnnotation = watch(
      'mP3ComponentModelData.isRedactionByAnnotation'
    );
    const mP3SpecifiedRedactionTagList = watch(
      'mP3ComponentModelData.specifiedRedactionTagList'
    );

    useEffect(() => {
      if (mP3ComponentModelData) trigger('mP3ComponentModelData');
    }, [
      mP3IsBurnRedactionSelected,
      mP3IsRedactionByAnnotation,
      mP3SpecifiedRedactionTagList,
      trigger,
    ]);

    // ── Section checked state ─────────────────────────────────────────────────
    const [sectionChecked, setSectionChecked] = useState<
      Record<string, boolean>
    >({
      DAT: true,
      Native: false,
      'TIFF / PDF': false,
      Text: false,
    });

    const isDATSelected = sectionChecked['DAT'] ?? true;
    const isNativeSelected = sectionChecked['Native'] ?? false;
    const isTIFFSelected = sectionChecked['TIFF / PDF'] ?? false;
    const isTextSelected = sectionChecked['Text'] ?? false;
    const isPDFSelected = false;
    const isArchiveSelected = false;
    const [isChildDirty, setIsChildDirty] = useState(false);
    const isHydratingComponentsRef = useRef(true);

    // ── API ───────────────────────────────────────────────────────────────────
    const {
      data: productionComponentsData,
      isLoading,
      error,
    } = useGetReProductionComponents(
      {
        SecurityGroupId: securityGroupId,
        moduletype: moduleType,
        parentsetid: productionSetId,
        productionId,
        exportProductionId: exportProductionId || 0,
      },
      !!securityGroupId && !!productionSetId && productionId > 0
    );

    const tagTreeData = useMemo(
      () =>
        productionComponentsData?.tagTree
          ? transformTagTreeData(productionComponentsData.tagTree)
          : [],
      [productionComponentsData]
    );

    useEffect(() => {
      if (!productionComponentsData) return;
      isHydratingComponentsRef.current = true;
      const model = productionComponentsData.productionComponentModel;

      setSectionChecked({
        DAT: model?.isDATSelected ?? true,
        Native: model?.isNativeSelected ?? false,
        'TIFF / PDF': model?.isTIFFSelected ?? false,
        Text: model?.isTextSelected ?? false,
      });

      // Sync section selections into RHF without marking the form dirty (API hydration).
      setValue('datSelected', model?.isDATSelected ?? true, {
        shouldDirty: false,
      });
      setValue('nativeSelected', model?.isNativeSelected ?? false, {
        shouldDirty: false,
      });
      setValue('tiffSelected', model?.isTIFFSelected ?? false, {
        shouldDirty: false,
      });
      setValue('textSelected', model?.isTextSelected ?? false, {
        shouldDirty: false,
      });

      if (productionComponentsData.fillerAudioFileList)
        dispatch(
          setFillerAudioFileList(productionComponentsData.fillerAudioFileList)
        );
      if (model?.mP3ComponentModelData)
        dispatch(setMP3DataComponent(model.mP3ComponentModelData));
      if (productionComponentsData.redactionTagList)
        dispatch(
          setRedactionTagList(productionComponentsData.redactionTagList)
        );
    }, [productionComponentsData, setValue, dispatch]);

    const completeComponentHydrationRef = useRef<() => void>(() => {});
    completeComponentHydrationRef.current = () => {
      reset(getValues(), {keepValues: true});
      setIsChildDirty(false);
      isHydratingComponentsRef.current = false;
      dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
    };

    const hydrationDebounceTimerRef = useRef<number | null>(null);

    const scheduleHydrationCompleteRef = useRef(() => {});
    scheduleHydrationCompleteRef.current = () => {
      if (hydrationDebounceTimerRef.current !== null) {
        window.clearTimeout(hydrationDebounceTimerRef.current);
      }
      isHydratingComponentsRef.current = true;
      hydrationDebounceTimerRef.current = window.setTimeout(() => {
        completeComponentHydrationRef.current();
        hydrationDebounceTimerRef.current = null;
      }, 200);
    };

    const scheduleHydrationComplete = useCallback(() => {
      scheduleHydrationCompleteRef.current();
    }, []);

    // After child components finish API hydration, clear stale dirty flags.
    // Debounce so late tagTree/DAT/TIFF hydration does not mark the form dirty.
    useEffect(() => {
      if (!productionComponentsData || isLoading) return;

      isHydratingComponentsRef.current = true;
      scheduleHydrationCompleteRef.current();

      return () => {
        if (hydrationDebounceTimerRef.current !== null) {
          window.clearTimeout(hydrationDebounceTimerRef.current);
          hydrationDebounceTimerRef.current = null;
        }
      };
    }, [productionComponentsData, isLoading, tagTreeData]);

    // // ── Optional sections ─────────────────────────────────────────────────────
    // const [isMP3FileSelection, setIsMP3FileSelection] = useState(false);
    // const [isTranslationsSelection, setIsTranslationsSelection] = useState(false);
    // const [optionalSectionsState, setOptionalSectionsState] = useState<
    //   {id: string; checked: boolean}[]
    // >([]);
    const containerRef = useRef<HTMLDivElement>(null);
    // ── TIFF handlers ─────────────────────────────────────────────────────────
    const [tiffGetConfig, setTiffGetConfig] = useState<
      (() => TiffComponentModel | undefined) | null
    >(null);
    const [tiffRefetch, setTiffRefetch] = useState<(() => void) | null>(null);

    // Sync TIFF values into RHF for validation/save — dirty state comes from onTiffDirtyChange only.
    const handleTiffPdfChange = (values: TiffPdfValues) => {
      setValue('tiffPdfData', values, {
        shouldValidate: true,
        shouldDirty: false,
      });
      trigger(['tiffPdfData', 'tiffSelected']);
    };

    const handleTiffDirtyChange = useCallback((dirty: boolean) => {
      if (isHydratingComponentsRef.current) return;
      setIsChildDirty(dirty);
    }, []);
    const handleTiffConfigAvailable = (fn: () => TiffComponentModel) =>
      setTiffGetConfig(() => fn);
    const handleTiffRefetchAvailable = (fn: () => void) =>
      setTiffRefetch(() => fn);

    // ── Native values ref ─────────────────────────────────────────────────────
    const nativeValuesRef = useRef<{
      fileGroups: string;
      tags: string;
      initialized: boolean;
    }>({
      fileGroups: '',
      tags: '',
      initialized: false,
    });

    const handleNativeFileGroupsAndTagsChange = useCallback(
      (fileGroups: string, tags: string) => {
        if (!nativeValuesRef.current.initialized && !fileGroups && !tags)
          return;

        nativeValuesRef.current = {fileGroups, tags, initialized: true};

        const currentFileGroups = getValues('nativeSelectedFileGroups');
        const currentTags = getValues('nativeSelectedTags');

        if (currentFileGroups !== fileGroups || currentTags !== tags) {
          setValue('nativeSelectedFileGroups', fileGroups, {
            shouldValidate: false,
            shouldDirty: !isHydratingComponentsRef.current,
          });
          setValue('nativeSelectedTags', tags, {
            shouldValidate: false,
            shouldDirty: !isHydratingComponentsRef.current,
          });
        }
      },
      [getValues, setValue]
    );

    // ── Save ──────────────────────────────────────────────────────────────────
    const [isSaved, setIsSaved] = useState(false);
    const {mutate: saveReProductionComponents} =
      useSaveReProductionComponents();

    useEffect(() => {
      isHydratingComponentsRef.current = true;
      dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
    }, [dispatch]);

    // Reset dirty when saved
    useEffect(() => {
      if (isSaved) {
        setIsChildDirty(false);
        setIsChildDirty(false);
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
      }
    }, [isSaved]);

    useEffect(() => {
      setIsSaved(false);
    }, [
      isDATSelected,
      isNativeSelected,
      isTIFFSelected,
      isTextSelected,
      // isTranslationsSelection,
      //isMP3FileSelection,
    ]);

    const buildSavePayload = useCallback(
      (data: ReProductionComponentsFormData, isMarkComplete: boolean) => {
        const realDatConfig = (window as any).getDatConfig
          ? (window as any).getDatConfig()
          : null;

        const datComponentModelData = {
          ansiFormatType: realDatConfig?.ansiFormatType,
          fieldSeparator: realDatConfig?.fieldSeparator,
          textQualifier: realDatConfig?.textQualifier,
          newLineSeparator: realDatConfig?.newLineSeparator,
          multiValue: realDatConfig?.multiValue,
          dateFormatID: realDatConfig?.dateFormatID,
          dateFormat: realDatConfig?.dateFormat,
          overwriteMasterDate: realDatConfig?.overwriteMasterDate,
          isAnsi: realDatConfig?.isAnsi,
          eTextFormat: realDatConfig?.eTextFormat,
          prependCarriageReturn: realDatConfig?.prependCarriageReturn,
          datFieldMappingDataList:
            (data as any).datFields?.map((field: any, index: number) => ({
              selectedTypeId:
                field.category === DAT_FIELD_CATEGORY_TAGS
                  ? DAT_FIELD_TYPE_TAGS
                  : DAT_FIELD_TYPE_FIELDS,
              selectedSourceId:
                field.category === DAT_FIELD_CATEGORY_TAGS
                  ? field.selectedTags?.map((tag: any) => tag.id).join(',') ||
                    ''
                  : field.sourceField,
              datField: field.datFieldHeaderName,
              offSet: '',
              isPrivileged: field.scrubPriv || false,
              isRedacted: field.scrubRedactions || false,
              sequenceNo: index + 1,
            })) || [],
        };

        const {textComponentData} = getValues();

        const nativeFileGroups = nativeValuesRef.current.initialized
          ? nativeValuesRef.current.fileGroups
          : (data.nativeSelectedFileGroups ?? '');

        const nativeTags = nativeValuesRef.current.initialized
          ? nativeValuesRef.current.tags
          : (data.nativeSelectedTags ?? '');

        const realNativeConfig = (window as any).getNativeConfigReProduction
          ? (window as any).getNativeConfigReProduction()
          : null;

        return {
          moduleType,
          isMarkComplete,
          isDATSelected,
          isNativeSelected,
          isTIFFSelected,
          isTextSelected,
          isPDFSelected,
          isArchiveSelected,
          isTranslation: false,
          isMP3FileSelected: false,
          productionId,
          productionSecurityGroupId: securityGroupId,
          datComponentModelData,
          nativeComponentModelData: {
            isSelectAllFileTypes: realNativeConfig?.isSelectAllFileTypes,
            produceLoadFile: realNativeConfig?.produceLoadFile,
            generateHTMLforEmails: realNativeConfig?.generateHTMLforEmails,
            produceNativeForTechDocs:
              realNativeConfig?.produceNativeForTechDocs,
            producedLockedFiles: realNativeConfig?.producedLockedFiles,
            protectionOnExcelFile: realNativeConfig?.protectionOnExcelFile,
            nativeComponentmodel: realNativeConfig?.nativeComponentmodel,
            nativeSelectedFileGroups: nativeFileGroups,
            nativeSelectedTags: nativeTags,
            eRedactedAndPrevilededFamily:
              realNativeConfig?.eRedactedAndPrevilededFamily,
            redactionSettings: realNativeConfig?.redactionSettings,
          },
          textComponentData,
          tiffComponentModelData: tiffGetConfig?.() ?? undefined,
          pdfComponentModelData: {},
          archiveFileComponentData: {},
        };
      },
      [
        isDATSelected,
        isNativeSelected,
        isTIFFSelected,
        isTextSelected,
        isPDFSelected,
        isArchiveSelected,
        productionId,
        securityGroupId,
        moduleType,
        tiffGetConfig,
        getValues,
      ]
    );

    const onSave = useCallback(
      (data: ReProductionComponentsFormData) => {
        saveReProductionComponents(buildSavePayload(data, false), {
          onSuccess: (response: any) => {
            setIsSaved(true);
            getStatus(
              true,
              ToastType.Success,
              response?.message || response?.data?.message
            );
            tiffRefetch?.();
          },
          onError: (error: any) => {
            getStatus(
              true,
              ToastType.Error,
              error?.response?.data?.message || error?.message
            );
          },
        });
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
      },
      [
        buildSavePayload,
        saveReProductionComponents,
        getStatus,
        tiffRefetch,
        dispatch,
      ]
    );

    const onNext = useCallback(
      (data: ReProductionComponentsFormData) => {
        saveReProductionComponents(buildSavePayload(data, true), {
          onSuccess: () => {
            getStatus(
              true,
              ToastType.Success,
              t(
                'productionsAndExports.createNewProduction.common.nextSuccessToast'
              )
            );
            tiffRefetch?.();
            goNext();
          },
          onError: (error: any) => {
            getStatus(
              true,
              ToastType.Error,
              error?.response?.data?.message || error?.message
            );
          },
        });
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
      },
      [
        buildSavePayload,
        saveReProductionComponents,
        getStatus,
        tiffRefetch,
        goNext,
        dispatch,
      ]
    );

    // ── Valid / Invalid handlers ──────────────────────────────────────────────
    const onValid = (data: any) => {
      if (data.action === FormAction.SAVE) onSave(data);
      if (data.action === FormAction.NEXT) onNext(data);
    };

    const onInvalid = () => {
      const COMMON_ERROR = t(
        'productionsAndExports.createNewProduction.datComponent.errors.validationFailed'
      );
      let message = '';
      let type: ToastType = ToastType.Error;
      const isNativeFileSettingsEditableForErrors =
        !isInProgressOrCompleted &&
        Boolean(rules?.native?.subSections?.fileSettings);

      if (errors.datSelected?.message) message = errors.datSelected.message;
      else if (Array.isArray(errors.datFields)) {
        const fe = errors.datFields.find(
          (f: any) => f?.datFieldHeaderName?.message
        );
        if (fe) message = fe.datFieldHeaderName.message;
      } else if ((errors.datFields as any)?.message) {
        message = (errors.datFields as any).message;
      }

      if (!message && errors.tiffPdfData) {
        const td = errors.tiffPdfData as any;
        if (td?.customBrandingData?.message) {
          message = td.customBrandingData.message;
          type = ToastType.Warning;
        } else if (td?.placeholderSettings?.message)
          message = td.placeholderSettings.message;
        else if (td?.placeholderSettings?.enabledforNativeDocs?.message) {
          message = td.placeholderSettings.enabledforNativeDocs.message;
          type = ToastType.Warning;
        } else if (td?.redactionSettings?.message) {
          message = td.redactionSettings.message;
          type = ToastType.Warning;
        } else if (td?.redactionSettings?.redactionDataList?.message)
          message = td.redactionSettings.redactionDataList.message;
        else message = COMMON_ERROR;
      } else if (
        !message &&
        isNativeFileSettingsEditableForErrors &&
        errors.nativeSelected?.message
      ) {
        message = String(errors.nativeSelected.message);
      }
      getStatus(true, type, String(message));
    };

    // ── Imperative handle ─────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
      save: () => {
        setValue('action', FormAction.SAVE);
        handleSubmit(onValid, onInvalid)();
      },
      next: () => {
        if (isInProgressOrCompleted) {
          goNext();
          return;
        }
        setValue('action', FormAction.NEXT);
        handleSubmit(onValid, onInvalid)();
      },
    }));

    // ── Button state ──────────────────────────────────────────────────────────
    const isSaveDisabled =
      isInProgressOrCompleted ||
      isSubmitting ||
      (!isDirty && !isChildDirty && isSaved);
    const isNextDisabled = isSubmitting || !isValid;

    useEffect(() => {
      if (isInProgressOrCompleted) {
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
        return;
      }
      if (isHydratingComponentsRef.current) return;

      const hasUnsavedChanges = isDirty || isChildDirty;
      dispatch(
        setReproductionIsFieldsChanged({isFieldsChanged: hasUnsavedChanges})
      );
    }, [isDirty, isChildDirty, isInProgressOrCompleted, dispatch]);
    useEffect(() => {
      if (isDATSelected) trigger(['datSelected', 'datFields']);
    }, [isDATSelected, trigger]);
    useEffect(() => {
      onStateChange({
        saveDisabled: isSaveDisabled,
        nextDisabled: isInProgressOrCompleted ? false : isNextDisabled,
      });
    }, [
      isSaveDisabled,
      isNextDisabled,
      isInProgressOrCompleted,
      onStateChange,
    ]);

    // ── Sections ──────────────────────────────────────────────────────────────
    const sections = useMemo(
      () => [
        {
          id: 'DAT',
          title: {
            primaryText: t(
              'productionsAndExports.createNewProduction.productionComponents.sectionTitles.dat'
            ),
            secondaryText: t(
              'productionsAndExports.createNewProduction.productionComponents.sectionTitles.required'
            ),
          },
          checked: true,
          content: (
            <DatComponent
              productionId={productionId}
              isProductionSet={isProductionSet}
              exportProductionId={exportProductionId}
              tagTreeData={tagTreeData}
              sharedHydrationRef={isHydratingComponentsRef}
              onHydrationComplete={scheduleHydrationComplete}
              onFieldsChange={(fields) => {
                setValue('datFields', fields, {
                  shouldDirty: !isHydratingComponentsRef.current,
                });
                trigger('datFields');
              }}
              mode={getSectionMode('DAT')}
              isTemplate={false}
              scrubEditable={scrubEditable}
              isReProduction={true}
            />
          ),
        },
        {
          id: 'Native',
          title: {
            primaryText: t(
              'productionsAndExports.createNewProduction.productionComponents.sectionTitles.native'
            ),
          },
          checked: sectionChecked['Native'] ?? false,
          content: (
            <NativeComponent
              fileGroupList={productionComponentsData?.fileGroupTypeList || []}
              tagTreeData={tagTreeData}
              redactionTagList={
                productionComponentsData?.redactionTagList || []
              }
              nativeRedactionStyleList={
                productionComponentsData?.nativeRedactionStyleList || []
              }
              commonMetaDataList={
                productionComponentsData?.commonMetaDataList || []
              }
              commonProductionFieldList={
                productionComponentsData?.commonProductionFieldList || []
              }
              productionId={productionId}
              isProductionSet={isProductionSet}
              onFileGroupsAndTagsChange={handleNativeFileGroupsAndTagsChange}
              mode={getSectionMode('Native')}
              isTemplate={false}
              disableGenerateLST={disableGenerateLST}
              isReProduction={true}
              reproductionType={reProductionType ?? undefined}
              editableNativeSubSections={editableNativeSubSections}
            />
          ),
        },
        {
          id: 'TIFF / PDF',
          title: {
            primaryText: t(
              'productionsAndExports.createNewProduction.productionComponents.sectionTitles.tiffPdf'
            ),
          },
          checked: sectionChecked['TIFF / PDF'] ?? false,
          content: (
            <TiffPdfComponent
              productionId={productionId}
              isProductionSet={isProductionSet}
              productionComponentsData={productionComponentsData}
              onChange={handleTiffPdfChange}
              onDirtyChange={handleTiffDirtyChange}
              sharedHydrationRef={isHydratingComponentsRef}
              onHydrationComplete={scheduleHydrationComplete}
              onConfigAvailable={handleTiffConfigAvailable}
              onRefetchAvailable={handleTiffRefetchAvailable}
              initialValues={{}}
              mode={getSectionMode('TIFF / PDF')}
              isTemplate={false}
              editableTiffSubSections={editableTiffSubSections}
              reproductionType={reProductionType ?? undefined}
              persistentNativePlaceholderBaselineRef={
                nativePlaceholderBaselineRef
              }
            />
          ),
        },
        {
          id: 'Text',
          title: {
            primaryText: t(
              'productionsAndExports.createNewProduction.productionComponents.sectionTitles.text'
            ),
          },
          checked: sectionChecked['Text'] ?? false,
          content: (
            <TextComponent
              ansiList={productionComponentsData?.ansiTypeList || []}
              productionId={productionId}
              isProductionSet={isProductionSet}
              mode={getSectionMode('Text')}
              isTemplate={false}
              isReProduction={true}
            />
          ),
        },
      ],
      [
        sectionChecked,
        productionId,
        isProductionSet,
        exportProductionId,
        tagTreeData,
        productionComponentsData,
        getSectionMode,
        scrubEditable,
        disableGenerateLST,
        reProductionType,
        editableTiffSubSections,
        editableNativeSubSections,
        handleNativeFileGroupsAndTagsChange,
      ]
    );

    // ── Loading / error ───────────────────────────────────────────────────────
    if (isLoading || error || !productionComponentsData) {
      return (
        <Box className="new-production-form-section">
          <Box className="new-production-form production-components">
            <ArrLoadingSpinner />
          </Box>
        </Box>
      );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
      <FormProvider {...methods}>
        <Box
          className="new-production-form-section"
          component="form"
          onSubmit={handleSubmit(onValid, onInvalid)}
        >
          <Box
            ref={containerRef}
            className={[
              'new-production-form production-components',
              're-production-components',
              're-production-components--ready',
              isInProgressOrCompleted
                ? 're-production-components--committed'
                : '',
              reProductionType
                ? `re-production-components--${reProductionType
                    .toLowerCase()
                    .replace(/_/g, '-')}`
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <ArrAccordionWithCheckbox
              sections={sections}
              onCheckChange={() => {}}
              disableCheckboxEdit={true}
              allowMultiple={true}
            />
          </Box>
        </Box>
      </FormProvider>
    );
  }
);

ReProductionComponents.displayName = 'ReProductionComponents';
export default ReProductionComponents;
